import { Box, Dialog, Divider, Grid, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import useSmartAndRegularAssets from "../../../api/hooks/useSmartAndRegularAssets";
import { scenarioSetKeys, stepKeys } from "../../../api/queryKeys";
import PrimaryButton from "../../../components/buttons/PrimaryButton";
import DisplayMode from "../../../components/DisplayMode";
import ClearFiltersButton from "../../../components/filters/ClearFiltersButton";
import FilterMenu from "../../../components/filters/FilterMenu";
import SearchFilter from "../../../components/filters/SearchFilter";
import SwitchFilter from "../../../components/filters/SwitchFilter";
import LoadingIcon from "../../../components/LoadingIcon";
import AppPage from "../../../components/structure/AppPage";
import { Toast } from "../../../components/Toast";
import { CustomerFeatureFlagsContext } from "../../../contexts/CustomerFeatureFlagsContext";
import { EnvFeatureFlagContext } from "../../../contexts/EnvFeaturesContext";
import { LogicNameFilter, PageDisplayMode } from "../../../utils/constants";
import DetectionAlertScenarios from "../detections/components/DetectionAlertScenarios";
import { infamousCVE } from "./components/Header/infamous_cves";
import RiskParameters from "./components/Header/RiskParameters";
import ScenarioHeader from "./components/Header/ScenariosHeader";
import ScenariosTable from "./components/ScenariosTable";
import ScenarioSetModal from "./graph/ScenarioSetModal";

export default function ScenariosView() {
  const colors = useColors();
  const { scenariosDemoMode } = useContext(EnvFeatureFlagContext);

  const {
    fetchStepsByScenarioSet,
    fetchAllScenarioSets,
    getAllDetectedStepIDs,
  } = useAPI();
  const [selectedScenarioSet, setSelectedScenarioSet] = useState(null);
  const [filteredScenariosSets, setFilteredScenariosSets] = useState([]);
  const [scenariosSetsRowsCard, setScenariosSetsRowsCard] = useState([]);
  const [alertActiveDetection, setAlertActiveDetection] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [displayMode, setDisplayMode] = useState(PageDisplayMode.GRID);
  const [filterLogic, setFilterLogic] = useState({
    stepIDs: LogicNameFilter.LOGIC_AND,
    involvedControl: LogicNameFilter.LOGIC_OR,
  });

  const { detectionsActive } = useContext(CustomerFeatureFlagsContext);

  const [filterOptions, setFilterOptions] = useState({
    criticalAssets: [],
    perimeter: ["Internet"],
    threatType: [],
    threatActor: [],
    threatCampaign: [],
    infamousCVE: [...infamousCVE],
    riskParameters: [],
    stepIDs: [],
    involvedControl: [],
    showActiveDetection: false,
  });
  const [appliedFilters, setAppliedFilters] = useState({
    criticalAssets: {},
    perimeter: { Internet: true },
    threatType: {},
    threatActor: {},
    threatCampaign: {},
    infamousCVE: {},
    riskParameters: scenariosDemoMode
      ? {
          probability: [25, 75],
          likelihood: [25, 75],
        }
      : {},
    stepIDs: {},
    involvedControl: {},
    showActiveDetection: false,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const criticalAssetFilter = location.state?.filterCriticalAsset;

  const {
    isLoadingAll: assetsFetchIsLoading,
    regularAndActiveSmartAsset: assetsWithEntities,
  } = useSmartAndRegularAssets();

  const assetIdFromLink = location.state?.assetId;
  const stepIDFromLink = location.state?.stepID;
  const controlIdFromLink = location.state?.controlID;

  const { data: fetchedSteps, isLoading: stepsFetchIsLoading } = useQuery({
    queryKey: stepKeys.stepsByScenarioSet(
      selectedScenarioSet?.locations_and_phases_and_asset
    ),
    enabled: selectedScenarioSet !== null,
    queryFn: async () => {
      try {
        const response = await fetchStepsByScenarioSet(
          selectedScenarioSet.locations_and_phases_and_asset
        );
        return response.data;
      } catch (err) {
        Toast.error(
          err.response?.data?.message ||
            "Error fetching steps for scenario set, please try again later"
        );

        return [];
      }
    },
  });

  const { data: detectedStepIDs } = useQuery({
    queryKey: stepKeys.detectedSteps(),
    enabled: detectionsActive !== false,
    queryFn: async () => {
      try {
        const response = await getAllDetectedStepIDs();
        return response.data;
      } catch (err) {
        return [];
      }
    },
  });

  const { data: allScenarioSets, isLoading: isLoadingScenarioSets } = useQuery({
    queryKey: scenarioSetKeys.all,
    queryFn: fetchAllScenarioSets,
    select: (response) => response?.data,
  });

  const getAllDetectedStepIDsByScenarioSet = (scenarioSet, detectedStepIDs) => {
    const scenarioSetDetectedSteps = scenarioSet?.step_ids?.filter((step_id) =>
      detectedStepIDs.includes(step_id)
    );
    return scenarioSetDetectedSteps;
  };

  useEffect(() => {
    const localSearchParams = new URLSearchParams(location.search);
    const selectedScenarioSetId = decodeURIComponent(
      localSearchParams.get("selected_scenario_set")
    );
    if (selectedScenarioSetId === "null" && scenariosDemoMode) {
      const handleKeyPress = (event) => {
        if (event.key === "d") {
          event.preventDefault();
          event.stopPropagation();
          setAlertActiveDetection(true);
        }
      };

      window.addEventListener("keydown", handleKeyPress);

      return () => {
        window.removeEventListener("keydown", handleKeyPress);
      };
    }
  }, [scenariosDemoMode]);

  useEffect(() => {
    if (criticalAssetFilter && assetsWithEntities) {
      setAppliedFilters((prevFilters) => ({
        ...prevFilters,
        criticalAssets: {
          [criticalAssetFilter]: true,
        },
      }));
    }
  }, [criticalAssetFilter, assetsWithEntities]);

  useEffect(() => {
    const selectedAssetId = assetIdFromLink;
    if (selectedAssetId && allScenarioSets && assetsWithEntities) {
      setAppliedFilters((prevFilters) => ({
        ...prevFilters,
        criticalAssets: {
          ...prevFilters.criticalAssets,
          [selectedAssetId]: true,
        },
      }));
    }

    const selectedStepID = stepIDFromLink;
    if (selectedStepID && allScenarioSets) {
      setAppliedFilters((prevFilters) => ({
        ...prevFilters,
        stepIDs: {
          ...prevFilters.stepIDs,
          [selectedStepID]: true,
        },
      }));
    }
  }, [location, allScenarioSets, assetsWithEntities]);

  useEffect(() => {
    if (controlIdFromLink && allScenarioSets) {
      setAppliedFilters((prevFilters) => ({
        ...prevFilters,
        involvedControl: {
          ...prevFilters.involvedControl,
          [controlIdFromLink]: true,
        },
      }));
    }
  }, [location, allScenarioSets]);

  useEffect(() => {
    if (assetsWithEntities) {
      const criticalAssets = assetsWithEntities.map((asset) => {
        return asset;
      });
      setFilterOptions((prevFilters) => ({
        ...prevFilters,
        criticalAssets: criticalAssets,
      }));
    }
  }, [assetsWithEntities]);

  useEffect(() => {
    if (allScenarioSets?.length > 0 && !isLoadingScenarioSets) {
      const distinctThreatTypes = [
        ...new Set(
          allScenarioSets?.flatMap(
            (scenarioSet) => scenarioSet.threat_intel.possible_threats || []
          )
        ),
      ];
      const distinctThreatActorNames = [
        ...new Set(
          allScenarioSets?.flatMap(
            (scenarioSet) =>
              scenarioSet.threat_intel.mitre_attack_groups.map(
                (group) => group.name
              ) || []
          )
        ),
      ];
      const distinctThreatCampaigns = [
        ...new Set(
          allScenarioSets?.flatMap(
            (scenarioSet) =>
              scenarioSet.threat_intel.mitre_attack_campaigns.map(
                (campaign) => campaign.name
              ) || []
          )
        ),
      ];
      const distinctInfamousCVEs = [
        ...new Set(
          allScenarioSets?.flatMap(
            (scenarioSet) => scenarioSet?.related_cves || [...infamousCVE]
          )
        ),
      ];
      const distinctStepIDs = [
        "All",
        ...new Set(
          allScenarioSets?.flatMap((scenarioSet) =>
            scenarioSet.step_ids.map((stepID) => stepID.toString())
          )
        ),
      ].sort((a, b) => a - b);
      const distinctControlIDs = [
        ...new Set(
          allScenarioSets?.flatMap((scenarioSet) =>
            Object.keys(scenarioSet?.remediations_info || {})
          )
        ),
      ].sort((a, b) => parseInt(a) - parseInt(b));

      const customOrder = ["Internet", "Endpoint"];
      const lastLocation = "Physical";
      const distinctInitialLocations = [
        ...new Set(
          allScenarioSets?.map((scenarioSet) => {
            const locationId =
              scenarioSet.locations_and_phases_and_asset.split("-")[0];
            return locationId;
          })
        ),
      ].sort((a, b) => {
        // Special case for "Physical" to always be at the end
        if (a === lastLocation) return 1;
        if (b === lastLocation) return -1;

        const orderA = customOrder.indexOf(a);
        const orderB = customOrder.indexOf(b);

        if (orderA !== -1 && orderB !== -1) {
          return orderA - orderB;
        }

        if (orderA !== -1) return -1;
        if (orderB !== -1) return 1;

        return a.localeCompare(b);
      });

      setFilterOptions((prevFilters) => ({
        ...prevFilters,
        threatType: [...distinctThreatTypes],
        threatActor: [...distinctThreatActorNames],
        threatCampaign: [...distinctThreatCampaigns],
        infamousCVE: [...distinctInfamousCVEs],
        stepIDs: [...distinctStepIDs],
        involvedControl: [...distinctControlIDs],
        perimeter: [...distinctInitialLocations],
      }));
      setFilteredScenariosSets(allScenarioSets);
    }
  }, [allScenarioSets, isLoadingScenarioSets]);

  useEffect(() => {
    if (filteredScenariosSets !== null) {
      const customOrder = ["Internet", "Endpoint"];
      const lastLocation = "Physical";

      const rowsToShow = filteredScenariosSets
        ?.map((scenarioSet, index) => ({
          id: scenarioSet.locations_and_phases_and_asset,
          scenarioName: scenarioSet.name,
          coverage: scenarioSet.coverage,
          reactFlow: scenarioSet,
        }))
        ?.sort((a, b) => {
          // Extract prefix from the id
          const initialLocationA = a.id.split("-")[0];
          const initialLocationB = b.id.split("-")[0];

          // Determine the order based on customOrder array
          const orderA = customOrder.indexOf(initialLocationA);
          const orderB = customOrder.indexOf(initialLocationB);

          // Special case for "Physical" to always be at the end
          if (initialLocationA === lastLocation) return 1;
          if (initialLocationB === lastLocation) return -1;

          // If both prefixes are in the custom order array, sort by custom order
          if (orderA !== -1 && orderB !== -1) {
            return orderA - orderB;
          }

          // If only one prefix is in the custom order array, it should come first
          if (orderA !== -1) return -1;
          if (orderB !== -1) return 1;

          // If neither prefix is in the custom order array, sort alphabetically
          if (initialLocationA < initialLocationB) return -1;
          if (initialLocationA > initialLocationB) return 1;

          // If prefixes are equal, sort by coverage
          return a.coverage - b.coverage;
        });
      // reactFlow is scenarioSet object with all the data required for drawing the graph
      const sortedRows = rowsToShow.sort((a, b) => {
        // First prioritize: by coverage (lower coverage first)
        if (a.reactFlow.coverage < b.reactFlow.coverage) return -1;
        if (a.reactFlow.coverage > b.reactFlow.coverage) return 1;

        // Then prioritize manual vs non-manual
        if (b.reactFlow?.is_manual && !a.reactFlow?.is_manual) return 1;
        if (!b.reactFlow?.is_manual && a.reactFlow?.is_manual) return -1;
        return 0;
      });
      const rowWithIndex = sortedRows?.map((row, index) => {
        return {
          ...row,
          index: index,
        };
      });
      setScenariosSetsRowsCard(rowWithIndex);
    }
  }, [filteredScenariosSets]);

  useEffect(() => {
    if (allScenarioSets) {
      let filtered = allScenarioSets;
      if (Object.keys(appliedFilters.criticalAssets).length > 0) {
        const scenarioSet_filter = Object.keys(
          appliedFilters.criticalAssets
        ).filter((key) => appliedFilters.criticalAssets[key] === true);
        if (scenarioSet_filter.length > 0) {
          filtered = filtered.filter((scenarioSet) =>
            scenarioSet_filter.includes(scenarioSet?.asset_id)
          );
        }
      }
      if (Object.keys(appliedFilters.threatType).length > 0) {
        const scenarioSet_filter = Object.keys(
          appliedFilters.threatType
        ).filter((key) => appliedFilters.threatType[key] === true);
        if (scenarioSet_filter.length > 0) {
          filtered = filtered.filter((scenarioSet) =>
            scenarioSet?.threat_intel.possible_threats.some((threat) =>
              scenarioSet_filter.includes(threat)
            )
          );
        }
      }
      if (Object.keys(appliedFilters.threatActor).length > 0) {
        const scenarioSet_filter = Object.keys(
          appliedFilters.threatActor
        ).filter((key) => appliedFilters.threatActor[key] === true);
        if (scenarioSet_filter.length > 0) {
          filtered = filtered.filter((scenarioSet) =>
            scenarioSet?.threat_intel.mitre_attack_groups.some((group) =>
              scenarioSet_filter.includes(group.name)
            )
          );
        }
      }
      if (Object.keys(appliedFilters.perimeter).length > 0) {
        const scenarioSet_filter = Object.keys(appliedFilters.perimeter).filter(
          (key) => appliedFilters.perimeter[key] === true
        );
        if (scenarioSet_filter.length > 0) {
          filtered = filtered.filter((scenarioSet) => {
            const initialLocation =
              scenarioSet.locations_and_phases_and_asset.split("-")[0];
            return scenarioSet_filter.includes(initialLocation);
          });
        }
      }
      if (Object.keys(appliedFilters.threatCampaign).length > 0) {
        const scenarioSet_filter = Object.keys(
          appliedFilters.threatCampaign
        ).filter((key) => appliedFilters.threatCampaign[key] === true);
        if (scenarioSet_filter.length > 0) {
          filtered = filtered.filter((scenarioSet) =>
            scenarioSet?.threat_intel.mitre_attack_campaigns.some((campaign) =>
              scenarioSet_filter.includes(campaign.name)
            )
          );
        }
      }
      if (Object.keys(appliedFilters.infamousCVE).length > 0) {
        const scenarioSet_filter = Object.keys(
          appliedFilters?.infamousCVE
        ).filter((key) => appliedFilters.infamousCVE[key] === true);
        if (scenarioSet_filter.length > 0) {
          filtered = filtered.filter((scenarioSet) =>
            scenarioSet?.related_cves?.some((cve) =>
              scenarioSet_filter.includes(cve)
            )
          );
        }
      }
      if (Object.keys(appliedFilters.stepIDs).length > 0) {
        const sources_filter = Object.keys(appliedFilters.stepIDs).filter(
          (key) => appliedFilters.stepIDs[key] === true
        );
        if (sources_filter.length > 0) {
          if (filterLogic["stepIDs"] === LogicNameFilter.LOGIC_OR) {
            filtered = filtered.filter((scenarioSet) =>
              scenarioSet?.step_ids.some((stepID) =>
                sources_filter.includes(stepID.toString())
              )
            );
          } else if (filterLogic["stepIDs"] === LogicNameFilter.LOGIC_AND) {
            filtered = filtered.filter((scenarioSet) =>
              sources_filter.every((stepID) =>
                scenarioSet?.step_ids.includes(parseInt(stepID))
              )
            );
          }
        }
      }
      if (Object.keys(appliedFilters.involvedControl).length > 0) {
        const controls_filter = Object.keys(
          appliedFilters.involvedControl
        ).filter((key) => appliedFilters.involvedControl[key] === true);
        if (controls_filter.length > 0) {
          filtered = filtered.filter((scenarioSet) =>
            controls_filter.some((controlID) =>
              Object.keys(scenarioSet?.remediations_info || {}).includes(
                controlID
              )
            )
          );
        }
      }
      if (appliedFilters.showActiveDetection) {
        filtered = filtered.filter(
          (scenarioSet) =>
            getAllDetectedStepIDsByScenarioSet(scenarioSet, detectedStepIDs)
              .length > 0
        );
      }

      setFilteredScenariosSets(filtered);
    }
  }, [appliedFilters, allScenarioSets, filterLogic]);

  const getAverageCoverage = () => {
    const totalCoverage = scenariosSetsRowsCard?.reduce(
      (acc, row) => acc + row.coverage * row.reactFlow.scenario_count,
      0
    );
    const totalScenarios = scenariosSetsRowsCard?.reduce(
      (acc, row) => acc + row.reactFlow.scenario_count,
      0
    );
    const averageCoverage = totalCoverage / totalScenarios;
    let roundedCoverage = Math.round(averageCoverage); // Use Math.round to round to the nearest integer

    // Handle edge cases
    if (totalScenarios === 0) {
      roundedCoverage = -1;
    }
    if (totalScenarios > 0 && averageCoverage === 0) {
      roundedCoverage = 1;
    } else {
      if (averageCoverage !== 0 && roundedCoverage === 0) {
        roundedCoverage = 1;
      } else if (averageCoverage !== 100 && roundedCoverage === 100) {
        roundedCoverage = 99;
      }
    }

    return roundedCoverage;
  };

  if (assetsFetchIsLoading) {
    return <LoadingIcon size={16} />;
  }

  const getScenarioHeaderData = () => {
    const uniqueAssetIds = new Set(
      scenariosSetsRowsCard?.map((row) => row.reactFlow.asset_id)
    );
    const assetsAtRisk =
      assetsWithEntities?.filter((asset) =>
        uniqueAssetIds.has(asset.asset_id)
      ) || [];

    return {
      coverage: getAverageCoverage() || 0,
      total_scenarios: scenariosSetsRowsCard?.length
        ? scenariosSetsRowsCard.length
        : 0,
      total_paths: scenariosSetsRowsCard?.reduce(
        (acc, row) => acc + row.reactFlow.scenario_count,
        0
      ),
      assets_at_risk: uniqueAssetIds.size,
      assets_at_risk_list: assetsAtRisk,
    };
  };

  const goToActiveDetection = () => {
    const params = new URLSearchParams(location.search);
    if (scenariosSetsRowsCard.length < 2) {
      return;
    }
    const detectionScenarioId = scenariosSetsRowsCard[1].id;
    const fullScenarioSet = allScenarioSets.find(
      (scenarioSet) =>
        scenarioSet.locations_and_phases_and_asset === detectionScenarioId
    );

    const scenarioSetId = encodeURIComponent(detectionScenarioId);
    params.set("selected_scenario_set", scenarioSetId);
    params.set("showActiveDetection", "true");
    setSearchParams(params.toString());
    setSelectedScenarioSet(fullScenarioSet);
    setAlertActiveDetection(false);
  };
  const resetFilters = () => {
    setAppliedFilters(
      Object.keys(appliedFilters).reduce((acc, key) => {
        if (typeof appliedFilters[key] === "string") {
          acc[key] = "";
        } else if (
          typeof appliedFilters[key] === "object" &&
          !Array.isArray(appliedFilters[key])
        ) {
          acc[key] = {};
        } else if (Array.isArray(appliedFilters[key])) {
          acc[key] = [];
        } else if (typeof appliedFilters[key] === "boolean") {
          acc[key] = false;
        }
        return acc;
      }, {})
    );
  };

  return (
    <AppPage title="Attack Surface">
      {assetsWithEntities?.length > 0 ? (
        <Box>
          <Grid container direction={"row"} gap={1} sx={{}}>
            <Grid
              container
              direction={"row"}
              alignItems={"center"}
              gap={3}
              wrap="nowrap"
            >
              <Grid
                item
                display={"flex"}
                flexDirection={"column"}
                gap={1}
                width={"100%"}
              >
                {/* filter options */}
                <Grid
                  container
                  display={"flex"}
                  alignItems={"center"}
                  sx={{
                    boxShadow: colors.entity_type_overview_top_shadow,
                    borderRadius: "12px",
                    p: "12px",
                    gap: 1,
                    backgroundColor: colors.grey_2percent,
                  }}
                >
                  {displayMode === PageDisplayMode.GRID && (
                    <>
                      <ScenarioHeader
                        data={getScenarioHeaderData()}
                        goToInvestigation={goToActiveDetection}
                        alertActiveDetection={alertActiveDetection}
                      />
                      <Box
                        sx={{
                          alignItems: "center",
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        <Divider
                          orientation="horizontal"
                          sx={{
                            borderColor: colors.divider,
                            width: "100%",
                          }}
                        />
                      </Box>
                    </>
                  )}

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <SearchFilter
                        filterName={"Critical Asset"}
                        fieldName={"criticalAssets"}
                        options={filterOptions?.criticalAssets?.map((asset) => {
                          return {
                            name: asset.asset_id,
                            label: asset.name,
                            displayName: asset.name,
                          };
                        })}
                        appliedFilters={appliedFilters}
                        setAppliedFilters={setAppliedFilters}
                        fullOptions={filterOptions.criticalAssets}
                      />
                      <SearchFilter
                        filterName={"Perimeter"}
                        fieldName={"perimeter"}
                        options={filterOptions.perimeter?.map((location) => {
                          return {
                            name: location,
                            label: location,
                            displayName: location,
                          };
                        })}
                        appliedFilters={appliedFilters}
                        setAppliedFilters={setAppliedFilters}
                        fullOptions={filterOptions.perimeter}
                      />
                      <FilterMenu
                        filterName={"Threat Type"}
                        fieldName={"threatType"}
                        options={filterOptions.threatType?.map((threat) => {
                          return {
                            name: threat,
                            label: threat,
                            displayName: threat,
                          };
                        })}
                        appliedFilters={appliedFilters}
                        setAppliedFilters={setAppliedFilters}
                        fullOptions={filterOptions.threatType}
                      />
                      <SearchFilter
                        filterName={"Threat Actor"}
                        fieldName={"threatActor"}
                        options={filterOptions.threatActor?.map((threat) => {
                          return {
                            name: threat,
                            label: threat,
                            displayName: threat,
                          };
                        })}
                        appliedFilters={appliedFilters}
                        setAppliedFilters={setAppliedFilters}
                        fullOptions={filterOptions.threatActor}
                      />
                      <SearchFilter
                        filterName={"Threat Campaign"}
                        fieldName={"threatCampaign"}
                        options={filterOptions.threatCampaign?.map((threat) => {
                          return {
                            name: threat,
                            label: threat,
                            displayName: threat,
                          };
                        })}
                        appliedFilters={appliedFilters}
                        setAppliedFilters={setAppliedFilters}
                        fullOptions={filterOptions.threatCampaign}
                      />

                      <SearchFilter
                        filterName={"Involved Control"}
                        fieldName={"involvedControl"}
                        options={filterOptions.involvedControl?.map(
                          (controlID) => {
                            return {
                              name: controlID,
                              label: (
                                <Typography variant="caption">
                                  Control #{controlID}
                                </Typography>
                              ),
                              displayName: "Control#" + controlID,
                            };
                          }
                        )}
                        appliedFilters={appliedFilters}
                        setAppliedFilters={setAppliedFilters}
                        fullOptions={filterOptions.involvedControl}
                      />
                      {scenariosDemoMode && (
                        <>
                          <SearchFilter
                            filterName={"CVE"}
                            fieldName={"infamousCVE"}
                            options={filterOptions.infamousCVE?.map((cve) => {
                              return {
                                name: cve,
                                label: cve,
                                displayName: cve,
                              };
                            })}
                            appliedFilters={appliedFilters}
                            setAppliedFilters={setAppliedFilters}
                            fullOptions={filterOptions.infamousCVE}
                          />
                          <RiskParameters
                            appliedFilters={appliedFilters}
                            setAppliedFilters={setAppliedFilters}
                          />
                        </>
                      )}
                      {detectionsActive && (
                        <SwitchFilter
                          filterName={"showActiveDetection"}
                          label={"Only Active Detection"}
                          appliedFilters={appliedFilters}
                          setAppliedFilters={setAppliedFilters}
                        />
                      )}
                      <ClearFiltersButton
                        appliedFilters={appliedFilters}
                        setAppliedFilters={setAppliedFilters}
                      />
                    </Box>
                    <DisplayMode
                      displayMode={displayMode}
                      setDisplayMode={setDisplayMode}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <ScenariosTable
                setSelectedScenarioSet={setSelectedScenarioSet}
                filteredScenariosSets={filteredScenariosSets}
                isLoadingScenarioSets={isLoadingScenarioSets}
                scenariosSetsRowsCard={scenariosSetsRowsCard}
                fullScreen={displayMode === PageDisplayMode.GRID}
                setDisplayMode={setDisplayMode}
                resetFilters={resetFilters}
              />
            </Grid>
          </Grid>
          <Dialog
            open={alertActiveDetection}
            onClose={() => {
              setAlertActiveDetection(false);
            }}
            maxWidth={false}
            PaperProps={{
              style: {
                borderRadius: "8px",
                overflow: "hidden",
                width: "800px",
              },
            }}
          >
            <DetectionAlertScenarios
              fullAlert={true}
              onClose={() => {
                setAlertActiveDetection(false);
              }}
              goToScenarios={goToActiveDetection}
            />
          </Dialog>
          <ScenarioSetModal
            steps={fetchedSteps}
            stepsFetchIsLoading={stepsFetchIsLoading}
            allScenarioSets={allScenarioSets}
            isLoadingScenarioSets={isLoadingScenarioSets}
            scenariosSetsRowsCard={scenariosSetsRowsCard}
            setSelectedScenarioSet={setSelectedScenarioSet}
            selectedScenarioSet={selectedScenarioSet}
          />
        </Box>
      ) : (
        <Box>
          <Typography>
            Entities must be added to an asset for using Attack Surface.
          </Typography>
          <PrimaryButton onClick={() => navigate("/inventory/assets")}>
            Identify your Critical Assets
          </PrimaryButton>
        </Box>
      )}
    </AppPage>
  );
}
